'use client';

import { useState, useEffect, useRef, type CSSProperties } from 'react';
import Image from 'next/image';
import { WCProduct } from '@/lib/woocommerce';
import { useCart } from '@/hooks/cart-context';
import { useModal } from '@/hooks/modal-context';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import Footer from '@/components/Footer';
import LoginModal from '@/components/LoginModal';
import Script from 'next/script';
import HorizontalCarousel from '@/components/HorizontalCarousel';
import ScrollingMedia from '@/components/ScrollingMedia';
import { parseImg } from '@/lib/parse-images';
import Hls from 'hls.js';

declare global {
  // Runtime-configurable values when embedding outside Next (e.g. WordPress)
  // eslint-disable-next-line no-var
  var BABALU_PETID:
    | undefined
    | {
        apiBase?: string;
        wpUrl?: string;
      };
}

const getBabaluPetIdConfig = () => {
  if (typeof window === 'undefined') return {};
  return (window as any).BABALU_PETID ?? {};
};

/** Bundle attivi solo se il plugin WP lo dice (API petid-bundles enabled).*/
const getEnableBundles = (bundleConfigFromWp: { enabled?: boolean } | null) => {
  return bundleConfigFromWp?.enabled === true;
};

const getWpUrl = () => {
  const cfg = getBabaluPetIdConfig() as any;
  return cfg.wpUrl ?? process.env.NEXT_PUBLIC_WP_URL ?? '';
};

const getApiBase = () => {
  const cfg = getBabaluPetIdConfig() as any;
  return cfg.apiBase ?? '';
};

const buildApiUrl = (pathname: string) => {
  const base = getApiBase();
  if (!base) return pathname;
  return `${base.replace(/\/$/, '')}${pathname.startsWith('/') ? '' : '/'}${pathname}`;
};

const pressLogos = [
  "/assets/Frame-1533209091.svg",
  "/assets/Frame-1533209092.svg",
  "/assets/Frame-1533209093.svg",
  "/assets/Frame-1533209196.svg",
  "/assets/Group-289651.svg",
  "/assets/Group-289652.svg",
  "/assets/Group-289653.svg",
  "/assets/Group-289655.svg",
  "/assets/loghi-press_0000_La_Repubblica.svg",
];
// VideoCard Component (simplified version without subtitle and avatar)
interface VideoCardProps {
  className?: string;
  posterSrc: string;
  videoSrc: string;
  autoPlay?: boolean;
  playOnViewport?: boolean;
}

const VideoCard: React.FC<VideoCardProps> = ({
  className = "",
  posterSrc,
  videoSrc,
  autoPlay = false,
  playOnViewport = false,
}) => {
  const [openModal, setOpenModal] = useState(false);
  const [isInViewport, setIsInViewport] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const modalVideoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const modalHlsRef = useRef<Hls | null>(null);

  // Intersection Observer per playOnViewport
  useEffect(() => {
    if (!playOnViewport || !containerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInViewport(true);
          } else {
            setIsInViewport(false);
          }
        });
      },
      {
        threshold: 0.5, // Quando almeno il 50% del video è visibile
      }
    );

    observer.observe(containerRef.current);

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, [playOnViewport]);

  // Gestisce il caricamento HLS per i video .m3u8 (autoplay o playOnViewport)
  useEffect(() => {
    const shouldLoad = autoPlay || playOnViewport;
    if (!shouldLoad || !videoRef.current) return;

    const video = videoRef.current;
    const isHLS = videoSrc.endsWith('.m3u8');

    // Cleanup precedente hls instance
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    if (isHLS) {
      if (Hls.isSupported()) {
        // Usa hls.js per browser che non supportano HLS nativamente
        const hls = new Hls({
          enableWorker: true,
          lowLatencyMode: true,
        });
        hls.loadSource(videoSrc);
        hls.attachMedia(video);
        hlsRef.current = hls;

        return () => {
          if (hlsRef.current) {
            hlsRef.current.destroy();
            hlsRef.current = null;
          }
        };
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        // Safari supporta HLS nativamente
        video.src = videoSrc;
      }
    } else {
      // Video MP4 normale
      video.src = videoSrc;
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [videoSrc, autoPlay, playOnViewport]);

  // Controlla la riproduzione del video quando entra/esce dal viewport
  useEffect(() => {
    if (!playOnViewport || !videoRef.current) return;

    const video = videoRef.current;

    if (isInViewport) {
      // Quando entra nel viewport, metti in play
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {
          // Ignora errori di autoplay
        });
      }
    } else {
      // Quando esce dal viewport, metti in pausa
      video.pause();
    }
  }, [playOnViewport, isInViewport]);

  // Gestisce il caricamento HLS per il video nel modal
  useEffect(() => {
    if (!openModal || !modalVideoRef.current) return;

    const video = modalVideoRef.current;
    const isHLS = videoSrc.endsWith('.m3u8');

    // Cleanup precedente hls instance
    if (modalHlsRef.current) {
      modalHlsRef.current.destroy();
      modalHlsRef.current = null;
    }

    if (isHLS) {
      if (Hls.isSupported()) {
        // Usa hls.js per browser che non supportano HLS nativamente
        const hls = new Hls({
          enableWorker: true,
          lowLatencyMode: true,
        });
        hls.loadSource(videoSrc);
        hls.attachMedia(video);
        modalHlsRef.current = hls;

        return () => {
          if (modalHlsRef.current) {
            modalHlsRef.current.destroy();
            modalHlsRef.current = null;
          }
        };
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        // Safari supporta HLS nativamente
        video.src = videoSrc;
      }
    } else {
      // Video MP4 normale
      video.src = videoSrc;
    }

    return () => {
      if (modalHlsRef.current) {
        modalHlsRef.current.destroy();
        modalHlsRef.current = null;
      }
    };
  }, [videoSrc, openModal]);

  // Blocca lo scroll quando il modal è aperto
  useEffect(() => {
    if (openModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [openModal]);

  // Funzione per modificare lo z-index del StickyBanner
  const toggleStickyBanner = (isOpen: boolean) => {
    const stickyBanner = document.getElementById('sticky-banner');
    if (stickyBanner) {
      stickyBanner.style.zIndex = isOpen ? '10' : '9999';
    }
  };

  // Evita che il click sull'overlay chiuda il video
  const stopPropagation = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <>
      {/* Anteprima (poster) nella card */}
      <div ref={containerRef} className={`relative overflow-hidden rounded-3xl ${className}`}>
        {autoPlay || playOnViewport ? (
          <video
            ref={videoRef}
            className="w-full h-full object-cover cursor-pointer"
            autoPlay={autoPlay && !playOnViewport}
            muted
            loop
            playsInline
            onClick={() => {
              setOpenModal(true);
              toggleStickyBanner(true);
            }}
          />
        ) : (
          <>
            <img
              src={posterSrc}
              alt="Anteprima video"
              className="w-full h-full object-cover"
            />
            <button
              onClick={() => {
                setOpenModal(true);
                toggleStickyBanner(true);
              }}
              className="absolute inset-0 flex items-center justify-center"
            >
              <img
                src="/assets/play-video.png"
                alt="Play"
              />
            </button>
          </>
        )}
      </div>
      {/* Popup (modal) con il video vero e proprio */}
      {openModal && (
        <div
          className="fixed inset-0 z-[10000] bg-black bg-opacity-80 flex items-center justify-center p-4"
          onClick={() => {
            setOpenModal(false);
            toggleStickyBanner(false);
          }}
        >
          <div
            className="relative max-w-sm sm:max-w-md bg-black rounded-2xl overflow-hidden shadow-2xl"
            onClick={stopPropagation}
          >
            {/* Close Button */}
            <button
              onClick={() => {
                setOpenModal(false);
                toggleStickyBanner(false);
              }}
              className="absolute top-4 right-4 z-10 bg-white bg-opacity-90 text-black rounded-full w-10 h-10 flex items-center justify-center hover:bg-opacity-100 transition-all shadow-lg"
              aria-label="Chiudi video"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Video */}
            <video
              ref={modalVideoRef}
              poster={posterSrc}
              className="w-full h-auto max-h-[90vh] object-contain"
              controls
              autoPlay
              playsInline
            />
          </div>
        </div>
      )}
    </>
  );
};

// FeatureCard Component
interface FeatureCardProps {
  title: string;
  description: string;
  icon: string;
  alt: string;
  className?: string;
}

const FeatureCard: React.FC<FeatureCardProps> = ({
  title,
  description,
  icon,
  alt,
  className = "",
}) => {
  return (
    <div className={`flex-shrink-0 text-center p-6 md:p-12 rounded-[32px] w-[300px] md:w-[350px] ${className}`} style={{ backgroundColor: '#026E780D' }}>
      <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
        <img
          src={icon}
          alt={alt}
          width={64}
          height={64}
          className="w-16 h-16"
        />
      </div>
      <p className="font-bold text-gray-1 mb-2 mobile-p2 md:mobile-p1">{title}</p>
      <p className="mobile-p4 md:mobile-p3 text-gray-1">{description}</p>
    </div>
  );
};

// FeatureBox Component
interface FeatureBoxProps {
  text: string;
  icon: string;
  alt: string;
  className?: string;
  classTextName?: string;
  subtitle?: string;
}

const FeatureBox: React.FC<FeatureBoxProps> = ({
  text,
  icon,
  alt,
  className = "",
  classTextName = "",
  subtitle = "",
}) => {
  return (
    <div
      className={`z-[99] rounded-[8px] p-[12px] flex items-center justify-end ${className} gap-4`}
      style={{
        backgroundColor: '#ffffff',
        borderWidth: '0.5px',
        borderStyle: 'solid',
        borderColor: 'rgba(2, 110, 120, 0.2)',
        zIndex: 100
      }}
    >
      <div className="w-full">
        <p
          className={`mobile-p5 md:mobile-p4 text-primary w-full ${classTextName}`}
          dangerouslySetInnerHTML={{ __html: text }}
        />
        {subtitle && (
          <p className={`mobile-p6 text-gray-2 mt-1 ${classTextName}`}>
            {subtitle}
          </p>
        )}
      </div>
      <Image
        src={icon}
        alt={alt}
        width={20}
        height={20}
        className="w-5 h-5 flex-shrink-0"
      />
    </div>
  );
};

// FeatureInfoCard Component
interface FeatureInfoCardProps {
  icon: string;
  alt: string;
  title: string;
  description: string;
  descriptionMobileClass?: string;
}

const FeatureInfoCard: React.FC<FeatureInfoCardProps> = ({
  icon,
  alt,
  title,
  description,
  descriptionMobileClass = "mobile-p6",
}) => {
  return (
    <div className="text-left">
      <div className="w-full md:w-12 h-12 mb-3 flex items-center justify-center md:justify-start">
        <Image
          src={icon}
          alt={alt}
          width={48}
          height={48}
          className="w-12 h-12"
        />
      </div>
      <p className="md:mobile-p5 mobile-p6 font-bold text-gray-1 mb-2 md:text-left text-center">{title}</p>
      <p className={`md:mobile-p5 ${descriptionMobileClass} text-gray-1 md:text-left text-center`}>{description}</p>
    </div>
  );
};

// VideoImageOverlay Component
interface VideoImageOverlayProps {
  videoUrl: string;
  imageSrc?: string;
  imageAlt?: string;
  imageWidth?: number;
  imageHeight?: number;
  videoWidth?: string;
  videoTranslateY?: string;
}

const VideoImageOverlay: React.FC<VideoImageOverlayProps> = ({
  videoUrl,
  imageSrc,
  imageAlt = "Descrizione PetID",
  imageWidth = 600,
  imageHeight = 600,
  videoWidth = '44%',
  videoTranslateY = '-50%',
}) => {
  return (
    <div className="relative">
      {imageSrc && (
        <Image
          src={imageSrc}
          alt={imageAlt}
          width={imageWidth}
          height={imageHeight}
          className="z-[99]"
          style={{
            position: 'relative',
          }}
        />
      )}
      <video
        src={videoUrl}
        style={{
          width: videoWidth,
          transform: `translate(-50%, ${videoTranslateY})`,
        }}
        className="absolute top-1/2 left-1/2"
        autoPlay
        loop
        muted
        playsInline
      />
    </div>
  );
};

import { getDynamicPrice, getCartItemsForQuantity, PRODUCT_ID as PETID_PRODUCT_ID } from '@/lib/bundle-pricing';
import { useAuth } from '@/hooks/auth-context';
import { auth } from '@/config/firebase';
import { Suspense } from 'react';

function PetIDPageContent() {
  const { state } = useCart();
  const { currentUser, userData, userLoggedIn } = useAuth();
  const { isOpen, closeModal, isRegistration, isCheckoutFlow } = useModal();
  const searchParams = useSearchParams();
  const [product, setProduct] = useState<WCProduct | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedQuantity, setSelectedQuantity] = useState<number>(1);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [bundleImageError, setBundleImageError] = useState<Record<number, boolean>>({});
  /** Dati bundle da WP: label = titolo card, subtitle = riga sotto il titolo */
  const [bundleConfigFromWp, setBundleConfigFromWp] = useState<{
    enabled?: boolean;
    subtitle?: string;
    show_discount_pct?: boolean;
    prices_include_tax: boolean;
    2?: { price: number; label: string; subtitle: string };
    3?: { price: number; label: string; subtitle: string };
  } | null>(null);

  // Helper function to calculate price with VAT (IVA inclusa)
  // IVA standard in Italia: 22%
  const getPriceWithVAT = (price: string | number): number => {
    const priceNum = typeof price === 'string' ? parseFloat(price || '0') : price;
    const vatRate = 0.22; // 22% IVA
    return priceNum * (1 + vatRate);
  };

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const isWpEmbed = typeof window !== 'undefined' && !!(window as any).BABALU_PETID;
        const url = isWpEmbed
          ? `${getWpUrl()}wp-json/babalu/v1/products/61`
          : buildApiUrl(`/api/shop/products/61`);

        const response = await fetch(url);
        const result = await response.json();

        if (result.success) {
          setProduct(result.data);

          // Check for hash after product loads
          if (window.location.hash === '#faq') {
            setTimeout(() => {
              document.getElementById('faq')?.scrollIntoView({ behavior: 'smooth' });
            }, 500);
          }
        }
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, []);

  useEffect(() => {
    const wpUrl = typeof window !== 'undefined' ? getWpUrl() : '';
    if (!wpUrl) return;
    const url = `${wpUrl.replace(/\/$/, '')}/wp-json/babalu/v1/petid-bundles`;
    fetch(url)
      .then((res) => res.json())
      .then((json) => {
        if (json?.success && json?.data) {
          const data = json.data as {
            enabled?: boolean;
            subtitle?: string;
            show_discount_pct?: boolean;
            prices_include_tax?: boolean;
            '2'?: { price?: number; label?: string; subtitle?: string };
            '3'?: { price?: number; label?: string; subtitle?: string };
          };
          const config: {
            enabled?: boolean;
            subtitle?: string;
            show_discount_pct?: boolean;
            prices_include_tax: boolean;
            2?: { price: number; label: string; subtitle: string };
            3?: { price: number; label: string; subtitle: string };
          } = {
            enabled: data.enabled !== false,
            subtitle: typeof data.subtitle === 'string' ? data.subtitle : undefined,
            show_discount_pct: data.show_discount_pct !== false,
            prices_include_tax: data.prices_include_tax ?? true
          };
          if (data['2'] && typeof data['2'].price === 'number') {
            config[2] = {
              price: data['2'].price,
              label: typeof data['2'].label === 'string' && data['2'].label.trim() !== '' ? data['2'].label.trim() : 'Babalù® PetID',
              subtitle: typeof data['2'].subtitle === 'string' && data['2'].subtitle.trim() !== '' ? data['2'].subtitle.trim() : '2x Medagliette – Risparmia con due medagliette'
            };
          }
          if (data['3'] && typeof data['3'].price === 'number') {
            config[3] = {
              price: data['3'].price,
              label: typeof data['3'].label === 'string' && data['3'].label.trim() !== '' ? data['3'].label.trim() : 'Babalù® PetID',
              subtitle: typeof data['3'].subtitle === 'string' && data['3'].subtitle.trim() !== '' ? data['3'].subtitle.trim() : '3x Medagliette – Il massimo risparmio con tre medagliette'
            };
          }
          if (config[2] || config[3]) setBundleConfigFromWp(config);
        }
      })
      .catch(() => {});
  }, []);

  // Funzione per rilevare Safari
  const isSafari = () => {
    if (typeof window === 'undefined') return false;
    const ua = navigator.userAgent;
    const isSafariUA = /^((?!chrome|android).)*safari/i.test(ua);
    const isSafariWebKit = /^((?!chrome|android).)*safari/i.test(ua) && /webkit/i.test(ua);
    return isSafariUA || isSafariWebKit || (window as any).safari !== undefined;
  };

  const proceedToCheckout = async () => {
    if (!product) {
      console.error('proceedToCheckout: product non disponibile');
        return;
      }

    // Mostra il loading nel bottone
    setIsCheckoutLoading(true);

    // Ottieni currentUser direttamente da Firebase auth invece di aspettare il contesto
    // Questo è più affidabile dopo il login perché Firebase aggiorna auth.currentUser immediatamente
    const getFirebaseUser = async (maxWait = 10000): Promise<any> => {
      const startTime = Date.now();
      let attempts = 0;
      while ((Date.now() - startTime) < maxWait) {
        attempts++;
        // Controlla direttamente Firebase auth.currentUser (più affidabile del contesto)
        const fbUser = auth?.currentUser;
        if (fbUser && fbUser.uid && fbUser.email) {
          return fbUser;
        }
        // Controlla anche il contesto come fallback
        if (currentUser && currentUser.uid && currentUser.email) {
          return currentUser;
        }
        // Aspetta un po' prima di controllare di nuovo
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      console.error(`User non disponibile dopo ${maxWait}ms e ${attempts} tentativi`, { 
        authCurrentUser: auth?.currentUser,
        contextCurrentUser: currentUser, 
        userLoggedIn 
      });
      return null;
    };

    try {
      // Ottieni l'utente direttamente da Firebase auth
      const fbUser = await getFirebaseUser();

      if (!fbUser) {
        console.error('User non disponibile dopo il login', { 
          authCurrentUser: auth?.currentUser,
          contextCurrentUser: currentUser, 
          userLoggedIn 
        });
        setIsCheckoutLoading(false);
        alert('Errore: impossibile identificare l\'utente. Per favore, riprova.');
        return;
      }

      // Aspetta un po' per assicurarsi che il token sia pronto
      await new Promise(resolve => setTimeout(resolve, 500));

        // Usa la funzione che calcola la combinazione ottimale
        const cartItems = getCartItemsForQuantity(selectedQuantity);

      // Usa woocommerce_customer_id da userData
      const wcCustomerId: number | null =
        userData?.woocommerce_customer_id && userData.woocommerce_customer_id > 0
          ? userData.woocommerce_customer_id
          : null;
      
      if (wcCustomerId) {
        console.log(`✅ Usando woocommerce_customer_id: ${wcCustomerId}`);
      } else {
        console.warn('⚠️ woocommerce_customer_id non trovato in userData. Il customer dovrebbe essere creato automaticamente dal backend durante la registrazione.');
      }

      // Sincronizza e naviga alla pagina checkout con l'URL WordPress
      try {
        // Raccogli tutti i query params dalla pagina corrente
        const currentQueryParams: Record<string, string> = {};
        searchParams.forEach((value, key) => {
          currentQueryParams[key] = value;
        });

        const { syncCartToWooCommerce } = await import('@/utils/sync-cart-to-woocommerce');
        const checkoutUrl = await syncCartToWooCommerce(
          cartItems.map(item => ({
            id: item.id,
            quantity: item.quantity
          })),
          {
            email: fbUser?.email || undefined,
            first_name: userData?.first_name || fbUser.displayName?.split(' ')[0] || undefined,
            last_name: userData?.last_name || fbUser.displayName?.split(' ').slice(1).join(' ') || undefined
          },
          wcCustomerId,
          currentQueryParams
        );
        
        if (checkoutUrl) {
          // Su Safari mostra il modal con link cliccabile (evita popup blocker)
          // Su altri browser naviga nella stessa pagina
          if (isSafari()) {
            setCheckoutUrl(checkoutUrl);
            setShowCheckoutModal(true);
            setIsCheckoutLoading(false);
          } else {
            // Naviga nella stessa pagina su browser non-Safari
            window.location.href = checkoutUrl;
          }
        } else {
          console.error('Checkout URL non ottenuto');
          setIsCheckoutLoading(false);
          alert('Errore durante l\'inizializzazione del checkout. Per favore, riprova.');
        }
      } catch (error) {
        console.error('Errore durante il redirect al checkout:', error);
        setIsCheckoutLoading(false);
        alert('Errore durante l\'inizializzazione del checkout: ' + (error instanceof Error ? error.message : 'Errore sconosciuto'));
      }
    } catch (error) {
      console.error('Errore generale in proceedToCheckout:', error);
      setIsCheckoutLoading(false);
      alert('Errore durante il processo di checkout. Per favore, riprova.');
    }
  };

  // Funzione per checkout guest (senza autenticazione) – disponibile per uso futuro
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const proceedToGuestCheckout = async () => {
    if (!product) {
      console.error('proceedToGuestCheckout: product non disponibile');
      return;
    }

    // Mostra il loading nel bottone
    setIsCheckoutLoading(true);

    try {
      // Usa la funzione che calcola la combinazione ottimale
      const cartItems = getCartItemsForQuantity(selectedQuantity);

      // Sincronizza il carrello per guest checkout (senza dati utente)
      try {
        // Raccogli tutti i query params dalla pagina corrente
        const currentQueryParams: Record<string, string> = {};
        searchParams.forEach((value, key) => {
          currentQueryParams[key] = value;
        });

        const { syncCartToWooCommerce } = await import('@/utils/sync-cart-to-woocommerce');
        const checkoutUrl = await syncCartToWooCommerce(
          cartItems.map(item => ({
            id: item.id,
            quantity: item.quantity
          })),
          {}, // Nessun dato billing per guest checkout
          null, // Nessun wcCustomerId per guest checkout
          currentQueryParams
        );
        
        if (checkoutUrl) {
          // Su Safari mostra il modal con link cliccabile (evita popup blocker)
          // Su altri browser naviga nella stessa pagina
          if (isSafari()) {
            setCheckoutUrl(checkoutUrl);
            setShowCheckoutModal(true);
            setIsCheckoutLoading(false);
          } else {
            // Naviga nella stessa pagina su browser non-Safari
            window.location.href = checkoutUrl;
          }
        } else {
          console.error('Checkout URL non ottenuto per guest checkout');
          setIsCheckoutLoading(false);
          alert('Errore durante l\'inizializzazione del checkout. Per favore, riprova.');
        }
      } catch (error) {
        console.error('Errore durante il redirect al checkout guest:', error);
        setIsCheckoutLoading(false);
        alert('Errore durante l\'inizializzazione del checkout: ' + (error instanceof Error ? error.message : 'Errore sconosciuto'));
      }
    } catch (error) {
      console.error('Errore generale in proceedToGuestCheckout:', error);
      setIsCheckoutLoading(false);
      alert('Errore durante il processo di checkout. Per favore, riprova.');
    }
  };

  const handleAddToCart = async () => {
    if (!product) return;

    // Controlla se ci sono prodotti Places nel carrello (ID 221-226)
    const PLACES_PRODUCT_IDS = [221, 222, 223, 224, 225, 226];
    const hasPlaces = state.items.some(item => PLACES_PRODUCT_IDS.includes(item.id));
    if (hasPlaces) {
      alert('Il carrello contiene già un pacchetto Places. Completa l\'acquisto del pacchetto Places prima di procedere con l\'acquisto delle medagliette.');
      return;
    }

    const isWpEmbed = typeof window !== 'undefined' && !!(window as any).BABALU_PETID;
    const wpUrl = (isWpEmbed ? getWpUrl() : process.env.NEXT_PUBLIC_WP_URL) || '';
    if (!wpUrl) {
      console.error('WooCommerce URL non configurato');
      alert('Errore durante l\'aggiunta al carrello. Per favore, riprova.');
      return;
    }

    setIsCheckoutLoading(true);

    try {
      // redirect GET a WooCommerce
      // add-to-cart. Una sola riga: prodotto 61 con quantity = selectedQuantity (1, 2 o 3).
      // Il plugin WP "Sconti PetID" applica 50€/70€ in carrello quando ci sono 2 o 3 PetID.
      const base = wpUrl.replace(/\/$/, '');
      const url = `${base}?add-to-cart=${PETID_PRODUCT_ID}&quantity=${selectedQuantity}`;
      window.location.href = url;
    } catch (error) {
      console.error('Errore durante l\'aggiunta al carrello:', error);
      alert('Errore durante l\'aggiunta al carrello. Per favore, riprova.');
      setIsCheckoutLoading(false);
    }
  };


  const handleImageClick = (imageSrc: string, index: number) => {
    setSelectedImage(imageSrc);
    setSelectedImageIndex(index);
    setIsImageModalOpen(true);
  };

  const closeImageModal = () => {
    setIsImageModalOpen(false);
    setSelectedImage(null);
  };

  const goToPreviousImage = () => {
    if (!product?.images || product.images.length <= 1) return;
    // Salta la prima immagine (indice 0), quindi partiamo da indice 1
    let newIndex;
    if (selectedImageIndex <= 1) {
      // Se siamo alla prima immagine visibile (indice 1), vai all'ultima
      newIndex = product.images.length - 1;
    } else {
      newIndex = selectedImageIndex - 1;
    }
    setSelectedImageIndex(newIndex);
    setSelectedImage(product.images[newIndex].src);
  };

  const goToNextImage = () => {
    if (!product?.images || product.images.length <= 1) return;
    // Salta la prima immagine (indice 0), quindi partiamo da indice 1
    let newIndex;
    if (selectedImageIndex >= product.images.length - 1) {
      // Se siamo all'ultima immagine, vai alla prima visibile (indice 1)
      newIndex = 1;
    } else {
      newIndex = selectedImageIndex + 1;
    }
    setSelectedImageIndex(newIndex);
    setSelectedImage(product.images[newIndex].src);
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isImageModalOpen) return;

      if (event.key === 'Escape') {
        closeImageModal();
      } else if (event.key === 'ArrowLeft') {
        goToPreviousImage();
      } else if (event.key === 'ArrowRight') {
        goToNextImage();
      }
    };

    if (isImageModalOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isImageModalOpen]);

  // Handle sticky bar visibility based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      const quantityBox = document.getElementById('box-quantity-petid');
      if (!quantityBox) return;

      const boxRect = quantityBox.getBoundingClientRect();
      const isScrolledPast = boxRect.bottom < 0;
      setShowStickyBar(isScrolledPast);
    };

    // Check on mount and on scroll
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);


  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqData = [
    {
      question: "Come funziona?",
      answer: "📲 Scansiona il QR Code sulla medaglietta: chi trova il tuo pet potrà visualizzare il suo profilo con foto, nome e informazioni utili."
    },
    {
      question: "Come rintraccio il mio pet?",
      answer: "📍 Ricevi subito una notifica con la posizione del tuo animale, anche se chi lo trova non ha l'app.\n☎️ Contattatevi in un tap: grazie ai numeri di emergenza inseriti, puoi essere raggiunto rapidamente."
    },
    {
      question: "Come si ricarica?",
      answer: "Niente batterie, solo una connessione sicura tra te, il tuo pet e la community di Babalù®. 💛"
    },
    {
      question: "Politica di reso",
      answer: "Per maggiori informazioni ed effettuare un reso, consulta la pagina di reso disponibile a <a href='/return' style='color: #026E78; text-decoration: underline;'>questo link</a>."
    }
  ];

  const frequentQuestionsData = [
    {
      question: "Cosa succede se il mio pet si perde?",
      answer: "Chiunque trovi il tuo pet può scansionare la medaglietta con la fotocamera del telefono (non serve l'app). Tu ricevi subito una notifica sull'app di Babalù®, con <strong>data, ora e posizione</strong>, e chi lo trova può contattarti immediatamente!"
    },
    {
      question: "Chi può vedere i dati del mio animale?",
      answer: "Solo chi scansiona il QR Code e solo le informazioni che tu hai scelto di rendere pubbliche (come nome, contatti o info mediche). Puoi <strong>modificare o nascondere</strong> i dati in qualsiasi momento direttamente dall'app."
    },
    {
      question: "Devo pagare un abbonamento o una ricarica per usarla?",
      answer: "Assolutamente no. La Babalù® PetID <strong>non richiede abbonamenti</strong> né costi nascosti. Si attiva una volta sola, in meno di 5 minuti, e resta funzionante per sempre."
    },
    {
      question: "E se cambio numero o voglio aggiornare i contatti di emergenza?",
      answer: "Puoi farlo quando vuoi dall'app Babalù® puoi <strong>aggiungere, modificare o eliminare</strong> i numeri da contattare in caso di smarrimento, in modo semplice e immediato."
    },
    {
      question: "È adatta anche ai gatti o solo ai cani?",
      answer: "È perfetta per entrambi 🐶🐱 È <strong>leggera, silenziosa, resistente all'acqua</strong> e il QR Code inciso a laser non sbiadirà mai, anche se il tuo pet ama rotolarsi ovunque."
    },
    {
      question: "Come funziona la spedizione?",
      answer: "<strong>Spedizione standard gratuita</strong> (4-6 giorni lavorativi)<br>La medaglietta arriva già pronta per essere attivata: bastano pochi minuti per collegare il profilo del tuo pet su Babalù®."
      // answer: "Puoi scegliere tra:<br>• <strong>Spedizione standard gratuita</strong> (4-6 giorni lavorativi)<br>• <strong>Spedizione express • 5€</strong> (1 giorno lavorativo, se ordini entro le 14:00 da lunedì a giovedì)<br>La medaglietta arriva già pronta per essere attivata: bastano pochi minuti per collegare il profilo del tuo pet su Babalù®."
    },
    {
      question: "Posso effettuare il reso della medaglietta?",
      answer: "Sì, puoi effettuare il reso della medaglietta entro 14 giorni dalla data di ricezione. Per maggiori informazioni ed effettuare un reso, consulta la pagina di reso disponibile a <a href='/return' style='color: #026E78; text-decoration: underline;'>questo link</a>."
    },
  ];

  const reviewsData = [
    {
      title: "È un'app davvero ben realizzata",
      content: "È un'app davvero ben realizzata, utile e piacevole da utilizzare. Offre diverse funzioni pensate per semplificare la gestione degli animali domestici. L'interfaccia è chiara e intuitiva, rendendo l'esperienza complessivamente molto positiva. Un'app consigliata a chi ama prendersi cura dei propri animali.",
      rating: 5
    },
    {
      title: "Innovativa",
      content: "Mi è piaciuta l'innovazione proposta, per la sicurezza dell'animale.",
      rating: 5
    },
    {
      title: "Completa e dinamica",
      content: "Completa e dinamica. I libretti sanitari dei miei animali ora sono tutti in un'unico posto 🔥 Gracias 🫶",
      rating: 5
    },
    {
      title: "Servizio clienti ottimo",
      content: "Abbiamo ricevuto un PetID non utilizzabile ma siamo stati seguiti dal servizio clienti in modo veloce e preciso per risolvere il problema. Servizio anche tramite whatsapp per cui semplice e immediato.",
      rating: 5
    }
  ];

  const featuresData = [
    {
      title: "Peso piuma",
      description: "Pesa meno di 10 grammi!",
      icon: "/assets/medal_res/cloud.svg",
      alt: "Peso piuma"
    },
    {
      title: "Anti rumore",
      description: "Non fa rumore per il benessere del tuo pet",
      icon: "/assets/medal_res/silence.svg",
      alt: "Anti rumore"
    },
    {
      title: "Impermeabile",
      description: "Grazie al suo rivestimento in metallo",
      icon: "/assets/medal_res/umbrella.svg",
      alt: "Impermeabile"
    },
    {
      title: "Inciso a laser",
      description: "Il QR code non svanirà mai",
      icon: "/assets/medal_res/idea.svg",
      alt: "Inciso a laser"
    },
    {
      title: "Multi utente",
      description: "Associa amici e familiari senza limiti",
      icon: "/assets/medal_res/meeting.svg",
      alt: "Multi utente"
    },
    {
      title: "Senza batterie",
      description: "Nessun bisogno di ricaricarla",
      icon: "/assets/medal_res/world-health-day.svg",
      alt: "Senza batterie"
    },
    {
      title: "3 misure incluse",
      description: "S, M e L per o ogni taglia",
      icon: "/assets/medal_res/measures.svg",
      alt: "3 misure incluse"
    }
  ];

  const mediaData = [
    {
      type: "video" as const,
      src: "https://vz-ccd007ce-e29.b-cdn.net/df296a33-bb0b-40d6-a9ee-0359a0777914/play_720p.mp4",
      width: 325,
      height: 550,
      alt: "Video 1",
      mobileWidth: 150,
      mobileHeight: 245,
      className: "rounded-2xl object-cover"
    },
    {
      type: "image" as const,
      src: parseImg("https://firebasestorage.googleapis.com/v0/b/balu-4523d.appspot.com/o/site_res%2Fmedal_res%2Fimg1.jpg?alt=media&token=0e3a7308-d185-463d-a3f1-56c5aa17b572", "big"),
      width: 335,
      height: 482,
      alt: "Image 1",
      mobileWidth: 150,
      mobileHeight: 215,
      className: "rounded-2xl object-cover"
    },
    {
      type: "video" as const,
      src: "https://vz-ccd007ce-e29.b-cdn.net/f612c300-839b-4b7d-aafc-840f94bba299/play_720p.mp4",
      width: 325,
      height: 550,
      alt: "Video 2",
      mobileWidth: 150,
      mobileHeight: 245,
      className: "rounded-2xl object-cover"
    },
    {
      type: "image" as const,
      src: parseImg("https://firebasestorage.googleapis.com/v0/b/balu-4523d.appspot.com/o/site_res%2Fmedal_res%2Fimg2.jpg?alt=media&token=855c1e3c-242c-4796-8900-4d7f16b40dc9", "big"),
      width: 335,
      height: 482,
      alt: "Image 2",
      mobileWidth: 150,
      mobileHeight: 215,
      className: "rounded-2xl object-cover"
    },
    {
      type: "video" as const,
      src: "https://vz-ccd007ce-e29.b-cdn.net/866449ca-d372-4c78-a9c3-d7ffa5fe21aa/playlist.m3u8",
      width: 325,
      height: 550,
      alt: "Video 3",
      mobileWidth: 150,
      mobileHeight: 245,
      className: "rounded-2xl object-cover"
    },
    {
      type: "image" as const,
      src: parseImg("https://firebasestorage.googleapis.com/v0/b/balu-4523d.appspot.com/o/site_res%2Fmedal_res%2Fimg3.jpg?alt=media&token=36e96ea3-99da-43c2-abc5-944ff6e33de6", "big"),
      width: 335,
      height: 482,
      alt: "Image 3",
      mobileWidth: 150,
      mobileHeight: 215,
      className: "rounded-2xl object-cover"
    },
    {
      type: "video" as const,
      src: "https://vz-ccd007ce-e29.b-cdn.net/a22f9c6d-c610-4889-a4b3-bd6b95ad4a18/play_720p.mp4",
      width: 325,
      height: 550,
      alt: "Video 4",
      mobileWidth: 150,
      mobileHeight: 245,
      className: "rounded-2xl object-cover"
    },
    {
      type: "image" as const,
      src: parseImg("https://firebasestorage.googleapis.com/v0/b/balu-4523d.appspot.com/o/site_res%2Fsocial_proof%2FSOCIAL%20PROOF1.jpeg?alt=media&token=a19d3eab-fdef-4de0-afc5-a69605c670d0", "big"),
      width: 335,
      height: 482,
      alt: "Social Proof 1",
      mobileWidth: 150,
      mobileHeight: 215,
      className: "rounded-2xl object-cover"
    },
    {
      type: "image" as const,
      src: parseImg("https://firebasestorage.googleapis.com/v0/b/balu-4523d.appspot.com/o/site_res%2Fsocial_proof%2FSOCIAL%20PROOF5.jpeg?alt=media&token=867af3f4-d2c4-4b36-a6a6-e2277c554c45", "big"),
      width: 335,
      height: 482,
      alt: "Social Proof 5",
      mobileWidth: 150,
      mobileHeight: 215,
      className: "rounded-2xl object-cover"
    },
    {
      type: "image" as const,
      src: parseImg("https://firebasestorage.googleapis.com/v0/b/balu-4523d.appspot.com/o/site_res%2Fsocial_proof%2FSOCIAL%20PROOF6.jpeg?alt=media&token=3b8c09b9-deeb-4305-87e9-c33567705825", "big"),
      width: 335,
      height: 482,
      alt: "Social Proof 6",
      mobileWidth: 150,
      mobileHeight: 215,
      className: "rounded-2xl object-cover"
    },
    {
      type: "image" as const,
      src: parseImg("https://firebasestorage.googleapis.com/v0/b/balu-4523d.appspot.com/o/site_res%2Fsocial_proof%2FSOCIAL%20PROOF7.jpeg?alt=media&token=5afa8443-cb02-4356-8615-b406accb4214", "big"),
      width: 335,
      height: 482,
      alt: "Social Proof 7",
      mobileWidth: 150,
      mobileHeight: 215,
      className: "rounded-2xl object-cover"
    },
    {
      type: "image" as const,
      src: parseImg("https://firebasestorage.googleapis.com/v0/b/balu-4523d.appspot.com/o/site_res%2Fsocial_proof%2FSOCIAL%20PROOF8.jpeg?alt=media&token=d47dbfe0-5fa0-48dd-8f42-ac1f6f97783c", "big"),
      width: 335,
      height: 482,
      alt: "Social Proof 8",
      mobileWidth: 150,
      mobileHeight: 215,
      className: "rounded-2xl object-cover"
    },
    {
      type: "image" as const,
      src: parseImg("https://firebasestorage.googleapis.com/v0/b/balu-4523d.appspot.com/o/site_res%2Fsocial_proof%2FSOCIAL%20PROOF10.jpeg?alt=media&token=4dceb5dc-5cc9-4bdb-bf82-8e181b1cba3c", "big"),
      width: 335,
      height: 482,
      alt: "Social Proof 10",
      mobileWidth: 150,
      mobileHeight: 215,
      className: "rounded-2xl object-cover"
    },
    {
      type: "image" as const,
      src: parseImg("https://firebasestorage.googleapis.com/v0/b/balu-4523d.appspot.com/o/site_res%2Fsocial_proof%2FSOCIAL%20PROOF11.jpeg?alt=media&token=a0f4c1bf-3264-44d1-9ec2-3b3e42aeb790", "big"),
      width: 335,
      height: 482,
      alt: "Social Proof 11",
      mobileWidth: 150,
      mobileHeight: 215,
      className: "rounded-2xl object-cover"
    },
    {
      type: "image" as const,
      src: parseImg("https://firebasestorage.googleapis.com/v0/b/balu-4523d.appspot.com/o/site_res%2Fsocial_proof%2FSOCIAL%20PROOF12.jpeg?alt=media&token=df0955d0-605a-4d81-9943-c5ea5e6d4879", "big"),
      width: 335,
      height: 482,
      alt: "Social Proof 12",
      mobileWidth: 150,
      mobileHeight: 215,
      className: "rounded-2xl object-cover"
    },
    {
      type: "image" as const,
      src: parseImg("https://firebasestorage.googleapis.com/v0/b/balu-4523d.appspot.com/o/site_res%2Fsocial_proof%2FSOCIAL%20PROOF14.jpeg?alt=media&token=9689b6de-068e-4643-b475-1ec6a849ae20", "big"),
      width: 335,
      height: 482,
      alt: "Social Proof 14",
      mobileWidth: 150,
      mobileHeight: 215,
      className: "rounded-2xl object-cover"
    },
    {
      type: "image" as const,
      src: parseImg("https://firebasestorage.googleapis.com/v0/b/balu-4523d.appspot.com/o/site_res%2Fsocial_proof%2FSOCIAL%20PROOF19.jpeg?alt=media&token=6cb7bc39-19b8-4b1d-947f-d03a058fa283", "big"),
      width: 335,
      height: 482,
      alt: "Social Proof 19",
      mobileWidth: 150,
      mobileHeight: 215,
      className: "rounded-2xl object-cover"
    },
    {
      type: "image" as const,
      src: parseImg("https://firebasestorage.googleapis.com/v0/b/balu-4523d.appspot.com/o/site_res%2Fsocial_proof%2FSOCIAL%20PROOF24.jpeg?alt=media&token=a0233d54-cec9-4204-93fe-46e70e72cfec", "big"),
      width: 335,
      height: 482,
      alt: "Social Proof 24",
      mobileWidth: 150,
      mobileHeight: 215,
      className: "rounded-2xl object-cover"
    },
  ];

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="animate-pulse">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gray-200 aspect-square rounded-lg"></div>
            <div className="space-y-4">
              <div className="h-8 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-6 bg-gray-200 rounded w-1/2"></div>
              <div className="h-32 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8 text-center">
        <h1 className="text-heading2 font-heading text-gray-900 mb-4">
          Prodotto non trovato
        </h1>
        <Link
          href="/shop/products"
          className="text-blue-600 hover:underline"
        >
          Torna al catalogo
        </Link>
      </div>
    );
  }


  // Structured Data for SEO
  const productSchema = product ? {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "Babalù® PetID - Medaglietta Antismarrimento",
    "description": "La prima medaglietta antismarrimento con i super poteri! Tecnologia e materiali 100% made in Italy. Oltre 50 funzionalità esclusive. QR code inciso a laser, senza batterie, impermeabile, peso piuma (meno di 10 grammi). Adatta per cani e gatti.",
    "image": product.images?.map(img => img.src) || ["https://petid.babalu.it/wp-content/uploads/2025/10/product.png"],
    "sku": "PETID-001",
    "mpn": "PETID-001",
    "category": "Accessori per animali domestici",
    "brand": {
      "@type": "Brand",
      "name": "Babalù®"
    },
    "manufacturer": {
      "@type": "Organization",
      "name": "Babalù®",
      "addressCountry": "IT"
    },
    "countryOfOrigin": {
      "@type": "Country",
      "name": "Italia"
    },
    "additionalProperty": [
      {
        "@type": "PropertyValue",
        "name": "Peso",
        "value": "Meno di 10 grammi"
      },
      {
        "@type": "PropertyValue",
        "name": "Materiale",
        "value": "Metallo e polimeri italiani"
      },
      {
        "@type": "PropertyValue",
        "name": "Tecnologia QR Code",
        "value": "Inciso a laser permanente"
      },
      {
        "@type": "PropertyValue",
        "name": "Batterie",
        "value": "Non richieste"
      },
      {
        "@type": "PropertyValue",
        "name": "Impermeabile",
        "value": "Sì"
      },
      {
        "@type": "PropertyValue",
        "name": "Adatta per",
        "value": "Cani e gatti"
      },
      {
        "@type": "PropertyValue",
        "name": "Funzionalità",
        "value": "Oltre 50 funzionalità esclusive"
      },
      {
        "@type": "PropertyValue",
        "name": "Made in",
        "value": "Italia"
      }
    ],
    "offers": {
      "@type": "Offer",
      "url": "https://babalu.it/petid",
      "priceCurrency": "EUR",
      "price": product.price,
      "priceValidUntil": new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      "availability": "https://schema.org/InStock",
      "shippingDetails": {
        "@type": "OfferShippingDetails",
        "shippingRate": {
          "@type": "MonetaryAmount",
          "value": "0",
          "currency": "EUR"
        },
        "shippingDestination": {
          "@type": "DefinedRegion",
          "addressCountry": "IT"
        },
        "deliveryTime": {
          "@type": "ShippingDeliveryTime",
          "businessDays": {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"]
          },
          "cutoffTime": "14:00",
          "handlingTime": {
            "@type": "QuantitativeValue",
            "minValue": 1,
            "maxValue": 1,
            "unitCode": "DAY"
          },
          "transitTime": {
            "@type": "QuantitativeValue",
            "minValue": 4,
            "maxValue": 6,
            "unitCode": "DAY"
          }
        }
      }
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "5",
      "reviewCount": "4",
      "bestRating": "5",
      "worstRating": "1"
    },
    "review": reviewsData.map(review => ({
      "@type": "Review",
      "author": {
        "@type": "Person",
        "name": "Pet Lover"
      },
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": review.rating.toString(),
        "bestRating": "5"
      },
      "reviewBody": review.content,
      "headline": review.title
    }))
  } : null;

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      ...faqData.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer.replace(/<[^>]*>/g, '').replace(/\n/g, ' ')
        }
      })),
      ...frequentQuestionsData.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer.replace(/<[^>]*>/g, '').replace(/\n/g, ' ')
        }
      }))
    ]
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://babalu.it"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "PetID",
        "item": "https://babalu.it/petid"
      }
    ]
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Babalù®",
    "url": "https://babalu.it",
    "logo": "https://babalu.it/assets/babalu-404.svg",
    "sameAs": [
      "https://www.facebook.com/babaluapp.it",
      "https://www.instagram.com/babalu.app/"
    ]
  };

  // HowTo Schema per AI - Come funziona la medaglietta
  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "Come attivare e usare la medaglietta Babalù® PetID",
    "description": "Guida completa per attivare e utilizzare la medaglietta antismarrimento PetID di Babalù®",
    "image": product?.images?.[0]?.src || "https://petid.babalu.it/wp-content/uploads/2025/10/product.png",
    "totalTime": "PT5M",
    "estimatedCost": {
      "@type": "MonetaryAmount",
      "currency": "EUR",
      "value": product?.price
    },
    "step": [
      {
        "@type": "HowToStep",
        "position": 1,
        "name": "Scarica l'app Babalù®",
        "text": "Scarica l'applicazione Babalù® dal tuo store (App Store o Google Play)",
        "image": "https://babalu.it/assets/babalu-404.svg"
      },
      {
        "@type": "HowToStep",
        "position": 2,
        "name": "Registra il tuo pet",
        "text": "Crea o collega il profilo del tuo animale domestico nell'app",
        "image": product?.images?.[0]?.src
      },
      {
        "@type": "HowToStep",
        "position": 3,
        "name": "Scansiona il QR Code",
        "text": "Scansiona il QR Code inciso sulla medaglietta con la fotocamera del telefono per collegarla al profilo",
        "image": product?.images?.[0]?.src
      },
      {
        "@type": "HowToStep",
        "position": 4,
        "name": "Completa l'attivazione",
        "text": "Inserisci i contatti di emergenza e le informazioni del pet. L'attivazione richiede meno di 2 minuti",
        "image": product?.images?.[0]?.src
      },
      {
        "@type": "HowToStep",
        "position": 5,
        "name": "Attacca la medaglietta",
        "text": "Attacca la medaglietta al collare del tuo pet. È leggera (meno di 10 grammi), impermeabile e resistente",
        "image": product?.images?.[0]?.src
      }
    ]
  };

  // Article Schema per AI - Tratta la pagina come contenuto informativo
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Babalù® PetID - La medaglietta antismarrimento con i superpoteri",
    "description": "Oltre 50 funzionalità esclusive. Tecnologia e materiali 100% Made in Italy. Spedizione gratuita in tutta Italia",
    "image": product?.images?.map(img => img.src) || ["https://petid.babalu.it/wp-content/uploads/2025/10/product.png"],
    "author": {
      "@type": "Organization",
      "name": "Babalù®"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Babalù®",
      "logo": {
        "@type": "ImageObject",
        "url": "https://babalu.it/assets/babalu-404.svg"
      }
    },
    "datePublished": "2024-01-01",
    "dateModified": new Date().toISOString().split('T')[0],
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://babalu.it/petid"
    },
    "keywords": "medaglietta cani, medaglietta gatti, PetID, antismarrimento, qr code pet, medaglietta smart, babalù petid, made in italy",
    "articleSection": "Prodotti",
    "inLanguage": "it-IT"
  };

  // WebPage Schema per AI - Migliora la comprensione della pagina
  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Babalù® PetID - La medaglietta antismarrimento con i superpoteri",
    "description": "Oltre 50 funzionalità esclusive. Tecnologia e materiali 100% Made in Italy. Spedizione gratuita in tutta Italia",
    "url": "https://babalu.it/petid",
    "inLanguage": "it-IT",
    "isPartOf": {
      "@type": "WebSite",
      "name": "Babalù®",
      "url": "https://babalu.it"
    },
    "about": {
      "@type": "Thing",
      "name": "Medaglietta antismarrimento per animali domestici",
      "description": "Medaglietta intelligente con QR code per cani e gatti, tecnologia italiana, senza batterie, impermeabile"
    },
    ...(productSchema && { "mainEntity": productSchema })
  };

  return (
    <>
      {/* Structured Data for SEO */}
      {productSchema && (
        <Script
          id="product-schema"
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(productSchema) }}
        />
      )}
      <Script
        id="faq-schema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <Script
        id="breadcrumb-schema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <Script
        id="organization-schema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }}
      />
      <Script
        id="howto-schema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(howToSchema) }}
      />
      <Script
        id="article-schema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Script
        id="webpage-schema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }}
      />

      {/* Trustpilot Script */}
      <Script
        id="trustpilot-widget"
        src="https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js"
        strategy="afterInteractive"
      />

      <div className="relative">
        {/* Background Gradient */}
        <div
          className="absolute top-0 left-0 right-0 z-0"
          style={{
            height: '300px',
          }}
        />

        {/* Hero Section */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
            {/* Video Column */}
            <div className="relative">
              {/* Speech Bubble - Top Left of Video  md:hidden*/}
              <div className="absolute z-50" style={{ top: '-53px' }}>
                <Image
                  src="/assets/medal_res/ordinaentro.svg"
                  alt="Ordina entro 1"
                  width={400}
                  height={80}
                />
              </div>

              <VideoCard
                className="w-full aspect-[9/16] md:h-[598px] z-40 object-cover mb-4"
                posterSrc="/assets/video-medal-placeholder.png"
                videoSrc="https://vz-ccd007ce-e29.b-cdn.net/1e79a91d-437b-42c4-aff2-1724d4692390/playlist.m3u8"
                autoPlay={true}
              />

              {/* Product Images Gallery */}
              {product && product.images && product.images.length > 1 && (
                <div className="grid grid-cols-5 gap-4">
                  {product.images.slice(1).map((image, index) => (
                    <div
                      key={image.id}
                      className="w-full rounded-xl overflow-hidden bg-gray-100 cursor-pointer"
                      style={{ aspectRatio: '0.68' }}
                      onClick={() => handleImageClick(image.src, index + 1)}
                    >
                      <Image
                        src={image.src}
                        alt={image.alt || `Product image ${index + 2}`}
                        width={80}
                        height={118}
                        className="object-cover w-full h-full hover:scale-105 transition-transform duration-200"
                        sizes="80px"
                        loading={index < 3 ? "eager" : "lazy"}
                        quality={60}
                        priority={index === 0}
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* FAQ Accordion Section - Desktop only */}
              <div className="mt-8 hidden md:block">
                <div className="space-y-3">
                  {faqData.map((item, idx) => {
                    const isOpen = openIndex === idx;
                    return (
                      <div
                        key={idx}
                        className={`bg-[#F9F8F8] p-4 rounded-lg transition-all duration-300`}
                      >
                        <div
                          className="flex justify-between items-center cursor-pointer"
                          onClick={() => toggleFAQ(idx)}
                        >
                          <p className="md:mobile-p3 mobile-p4 font-bold text-[#333333]">
                            {item.question}
                          </p>
                          <p className="text-[#4F4F4F] md:mobile-p4 mobile-p5">
                            <img
                              src={
                                isOpen
                                  ? "/assets/home-su.svg"
                                  : "/assets/home-giu.svg"
                              }
                              alt="freccia su/giu"
                              className="w-5 h-5"
                            />
                          </p>
                        </div>
                        <div
                          className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                            }`}
                        >
                          <div
                            className="font-paragraph mt-3 text-[#4F4F4F] text-md leading-relaxed"
                            dangerouslySetInnerHTML={{ __html: item.answer }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Product Info */}
            <div className="flex flex-col justify-center self-start">
              {/* Rating */}
              {/* <div className="flex items-center mb-6">
                <div className="flex items-center">
                  {[...Array(5)].map((_, i) => (
                    <svg
                      key={i}
                      className="w-6 h-6 text-yellow-400 fill-current"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                  ))}
                </div>
                <span className="text-gray-600 ml-3 font-paragraph font-normal" style={{ fontSize: '11.72px', lineHeight: '15.62px' }}>4.78/5 SU +1.500 RECENSIONI</span>
              </div> */}

              {/* Title */}
              <h1 className="sr-only">Babalù® PetID - La medaglietta antismarrimento con i superpoteri</h1>

              {/* Chips */}
              {/* <div className="flex gap-2 mb-4">
                <div
                  className="flex items-center gap-2 px-3 py-1.5 rounded-full"
                  style={{ background: '#E7000B' }}
                >
                  <Image
                    src="/assets/medal_res/flash.svg"
                    alt="Flash"
                    width={16}
                    height={16}
                    className="flex-shrink-0"
                  />
                  <p className="mobile-p4 text-white uppercase">BLACK FRIDAY</p>
                </div>
                <div
                  className="flex items-center gap-2 px-3 py-1.5 rounded-full"
                  style={{ background: '#F0B100' }}
                >
                  <Image
                    src="/assets/medal_res/time.svg"
                    alt="Time"
                    width={16}
                    height={16}
                    className="flex-shrink-0"
                  />
                  <p className="mobile-p4 text-black uppercase">OFFERTA LIMITATA</p>
                </div>
              </div> */}

              <h3 className="md:mobile-h3 mobile-h4 font-bold ">
                <span className="text-black">Babalù®</span>{' '}
                <span className="text-primary">PetID</span>
              </h3>

              {/* Description */}
              <p className="md:mobile-p3 mobile-p4 text-gray-1 mb-8">
                La medaglietta antismarrimento con i super poteri: oltre 50 funzionalità esclusive! Tecnologia e materiali 100% Made in Italy.
              </p>

              {/* Price */}
              <div className="mb-6">
                {(() => {
                  if (getEnableBundles(bundleConfigFromWp)) {
                    // Bundle enabled: use the previous logic
                    if (selectedQuantity === 1) {
                      // For single quantity, use product prices directly
                      const hasDiscount = product?.sale_price && product.sale_price !== product.regular_price;
                      const priceWithVAT = getPriceWithVAT(product?.price || '0');
                      const regularPriceWithVAT = getPriceWithVAT(product?.regular_price || product?.price || '0');

                      return (
                        <>
                          {hasDiscount && (
                            <h5
                              className="line-through font-bold"
                              style={{ color: '#bbbbbb' }}
                            >
                              €{regularPriceWithVAT.toFixed(2)}
                            </h5>
                          )}
                          <div className="flex items-center gap-3 flex-wrap">
                            <h4 className="md:mobile-h4 mobile-h5 text-black">€{priceWithVAT.toFixed(2)}</h4>
                            <span className="inline-flex items-center justify-center text-center px-3 py-1 rounded-full bg-white text-primary border border-primary/30 shadow-sm mobile-small font-medium">
                              3 misure incluse
                            </span>
                          </div>
                          <span className="paragraph-4 text-gray-2 mt-1.5 block">🇮🇹 | Spedizione standard senza costi aggiuntivi in tutta <strong>Italia</strong>!</span>
                        </>
                      );
                    } else {
                      // For bundles, use dynamic pricing
                      const pricing = getDynamicPrice(selectedQuantity);
                      const hasDiscount = pricing.regular_price && pricing.regular_price !== pricing.price;
                      const priceWithVAT = getPriceWithVAT(pricing.price);
                      const regularPriceWithVAT = getPriceWithVAT(pricing.regular_price || pricing.price);

                      return (
                        <>
                          {hasDiscount && (
                            <h5
                              className="line-through font-bold"
                              style={{ color: '#bbbbbb' }}
                            >
                              €{regularPriceWithVAT.toFixed(2)}
                            </h5>
                          )}
                          <div className="flex items-center gap-3 flex-wrap">
                            <h4 className="md:mobile-h4 mobile-h5 text-black">€{priceWithVAT.toFixed(2)}</h4>
                            <span className="inline-flex items-center justify-center text-center px-3 py-1 rounded-full bg-white text-primary border border-primary/30 shadow-sm mobile-small font-medium">
                              3 misure incluse
                            </span>
                          </div>
                          {selectedQuantity > 3 && pricing.breakdown && (
                            <p className="paragraph-6 text-gray-600 mt-2">
                              ({pricing.breakdown})
                            </p>
                          )}
                          <p className="md:mobile-p4 mobile-p5 text-gray-2 mt-1.5 block">🇮🇹 | Spedizione gratuita in tutta Italia!</p>
                        </>
                      );
                    }
                  } else {
                    // Bundle disabled: calculate price based on quantity * unit price
                    const hasDiscount = product?.sale_price && product.sale_price !== product.regular_price;
                    const unitPriceWithVAT = getPriceWithVAT(product?.price || '0');
                    const unitRegularPriceWithVAT = getPriceWithVAT(product?.regular_price || product?.price || '0');
                    const totalPrice = unitPriceWithVAT * selectedQuantity;
                    const totalRegularPrice = unitRegularPriceWithVAT * selectedQuantity;

                    return (
                      <>
                        {hasDiscount && (
                          <h5
                            className="line-through font-bold"
                            style={{ color: '#bbbbbb' }}
                          >
                            €{totalRegularPrice.toFixed(2)}
                          </h5>
                        )}
                        <div className="flex items-center gap-3 flex-wrap">
                          <h4 className="md:mobile-h4 mobile-h5 text-black">€{totalPrice.toFixed(2)}</h4>
                          <span className="inline-flex items-center justify-center text-center px-3 py-1 rounded-full bg-white text-primary border border-primary/30 shadow-sm mobile-small font-medium">
                            3 misure incluse
                          </span>
                        </div>
                        <span className="paragraph-4 text-gray-2 mt-1.5 block">🇮🇹 | Spedizione standard gratis in tutta <strong>Italia</strong></span>
                      </>
                    );
                  }
                })()}
              </div>

              {/* Features */}
              <div className="grid grid-cols-3 gap-6 mb-8">
                <FeatureInfoCard
                  icon="/assets/rocket.png"
                  alt="Attivazione immediata"
                  title="Attivazione immediata"
                  description="Pronta all&apos;uso in meno di 2 minuti"
                />
                <FeatureInfoCard
                  icon="/assets/booklet.png"
                  alt="Tutti i dati del tuo pet"
                  title="Tutti i dati del tuo pet"
                  description="Libretto, contatti e tanto altro"
                />
                <FeatureInfoCard
                  icon="/assets/world.png"
                  alt="Internazionale"
                  title="Internazionale"
                  description="La PetID Babalù® non ha confini"
                />
              </div>

              {/* ENPA Recommendation Banner */}
              <div className="mb-8">
                <div
                  className="rounded-2xl p-4 md:p-6"
                  style={{
                    backgroundColor: '#FFFFFF',
                    backgroundImage: 'linear-gradient(360deg, rgba(0, 158, 150, 0.1) -10.5%, rgba(255, 255, 255, 0) 44.11%, rgba(0, 158, 150, 0.1) 118.71%)'
                  }}
                >
                  <a
                    href="#enpa-collaboration"
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById('enpa-collaboration')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                    className="flex items-center justify-center gap-3 md:gap-4 cursor-pointer hover:opacity-80 transition-opacity"
                  >
                    <Image
                      src="/assets/medal_res/enpa-little.svg"
                      alt="ENPA Logo"
                      width={60}
                      height={60}
                      className="w-12 h-12 md:w-16 md:h-16 object-contain"
                    />
                    <p className="text-center mobile-p3 md:mobile-p2 text-primary font-bold flex items-center justify-center gap-2">
                      Babalù PetID sostiene ENPA
                      <Image
                        src="/assets/info-circle.svg"
                        alt="info"
                        width={20}
                        height={20}
                        className="w-4 h-4 md:w-5 md:h-5 object-contain flex-shrink-0"
                      />
                    </p>
                  </a>
                </div>
              </div>

              {/* Quantity & Add to Cart */}
              <div
                className="p-3 md:p-6 rounded-lg"
                style={{
                  background: 'linear-gradient(180.08deg, #E5F2F2 -20.07%, rgba(255, 255, 255, 0) 99.93%), linear-gradient(0deg, rgb(255, 255, 255), rgb(255, 255, 255))',
                  border: '1px solid #BDBDBD'
                }}
              >
                {/* Title + optional subtitle from WP */}
                <div className="mb-4">
                  <h6 className="text-gray-1">
                    {getEnableBundles(bundleConfigFromWp) ? 'Scegli il tuo bundle 🐶 🐱' : 'Scegli la quantità 🐶 🐱'}
                  </h6>
                  {bundleConfigFromWp?.subtitle?.trim() && (
                    <p className="text-gray-2 mobile-small mt-1 mb-0">{bundleConfigFromWp.subtitle}</p>
                  )}
                </div>

                {/* Quantity Options */}
                {getEnableBundles(bundleConfigFromWp) ? (
                  // Bundle enabled: mostra le opzioni 1x, 2x, 3x
                  [1, 2, 3].map((qty) => {
                    const isSelected = selectedQuantity === qty;

                    const wpBundle = qty !== 1 ? bundleConfigFromWp?.[qty as 2 | 3] : null;
                    let pricing: { price: string; regular_price: string };
                    if (qty === 1) {
                      pricing = {
                        price: product?.price || '0',
                        regular_price: product?.regular_price || product?.price || '0'
                      };
                    } else if (wpBundle) {
                      const fallback = getDynamicPrice(qty);
                      pricing = {
                        price: String(wpBundle.price),
                        regular_price: fallback.regular_price ?? String(wpBundle.price)
                      };
                    } else {
                      const fallback = getDynamicPrice(qty);
                      pricing = {
                        price: fallback.price,
                        regular_price: fallback.regular_price ?? fallback.price
                      };
                    }

                    const hasDiscount = pricing.regular_price && pricing.regular_price !== pricing.price;
                    const showPriceWithVat = !(qty !== 1 && wpBundle && bundleConfigFromWp?.prices_include_tax);
                    const toDisplayPrice = (p: string) =>
                      showPriceWithVat ? getPriceWithVAT(p) : parseFloat(p) || 0;

                    const productImage = product?.images?.[0]?.src || '/assets/petid-icon.svg';
                    const useFallbackImage = bundleImageError[qty];
                    const getBundleImage = () => {
                      if (useFallbackImage) return productImage;
                      switch (qty) {
                        case 2:
                          return '/assets/medal_res/petidbundle-2.png';
                        case 3:
                          return '/assets/medal_res/petidbundle-3.png';
                        default:
                          return productImage;
                      }
                    };

                    const cardTitle = qty === 1 ? 'Babalù® PetID' : (wpBundle?.label ?? bundleConfigFromWp?.[qty as 2 | 3]?.label ?? 'Babalù® PetID');
                    const cardSubtitle = qty === 1
                      ? '1x Medaglietta – Medaglietta anti-smarrimento'
                      : (wpBundle?.subtitle ?? bundleConfigFromWp?.[qty as 2 | 3]?.subtitle ?? (qty === 2 ? '2x Medagliette – Risparmia con due medagliette' : '3x Medagliette – Il massimo risparmio con tre medagliette'));

                    return (
                      <div
                        key={qty}
                        onClick={() => setSelectedQuantity(qty)}
                        className={`flex items-center justify-between gap-4 p-4 rounded-xl bg-white cursor-pointer transition-all mb-3 ${isSelected
                          ? 'border-2 border-primary shadow-md'
                          : 'border-2 border-transparent hover:border-gray-300'
                          }`}
                      >
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0" style={{ background: '#EAF3F4' }}>
                            <Image
                              style={{ background: '#EAF3F4' }}
                              src={getBundleImage()}
                              alt={`${qty}x ${qty > 1 ? 'Medagliette' : 'Medaglietta'}`}
                              width={128}
                              height={128}
                              className="w-full h-full object-cover"
                              quality={90}
                              onError={() => setBundleImageError((prev) => ({ ...prev, [qty]: true }))}
                            />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="mobile-p6 text-black font-bold">{cardTitle}</p>
                            <p className="text-gray-2 mobile-small mt-0.5">{cardSubtitle}</p>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2 flex-shrink-0">
                          <div className="text-right">
                            {hasDiscount && (
                              <p className="mobile-p6 font-bold text-black line-through text-gray-2">
                                €{toDisplayPrice(pricing.regular_price || '0').toFixed(2)}
                              </p>
                            )}
                            <p className="mobile-p6 font-bold text-black">
                              €{toDisplayPrice(pricing.price).toFixed(2)}
                            </p>
                          </div>
                          {hasDiscount && (bundleConfigFromWp?.show_discount_pct !== false) && (
                            <div className="bg-primary text-white px-3 py-1 rounded-full font-bold small">
                              {Math.round(((parseFloat(pricing.regular_price || '0') - parseFloat(pricing.price)) / parseFloat(pricing.regular_price || '0')) * 100)}% SCONTO
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  // Bundle disabled: mostra input con +/- buttons
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 p-4 rounded-xl bg-white border-2 border-gray-200">
                    <div className="flex items-center gap-3 flex-1">
                      <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                        <Image
                          style={{
                            background: "#EAF3F4"
                          }}
                          src={product?.images?.[0]?.src || "/assets/petid-icon.svg"}
                          alt="Medaglietta"
                          width={128}
                          height={128}
                          className="w-full h-full object-cover"
                          quality={90}
                        />
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <p className="text-black mobile-p6 font-bold">Babalù® PetID</p>
                        <p className="text-gray-2 mobile-small">Medaglietta anti-smarrimento</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between md:justify-end gap-4 w-full md:w-auto">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setSelectedQuantity(Math.max(1, selectedQuantity - 1))}
                          className="w-8 h-8 rounded-full bg-button-bg flex items-center justify-center hover:bg-gray-100 transition-colors"
                        >
                          <p className="mobile-p6">−</p>
                        </button>
                        <p className="w-8 text-center mobile-p6 text-black">
                          {selectedQuantity}
                        </p>
                        <button
                          type="button"
                          onClick={() => setSelectedQuantity(selectedQuantity + 1)}
                          className="w-8 h-8 rounded-full bg-button-bg flex items-center justify-center hover:bg-gray-100 transition-colors"
                        >
                          <p className="mobile-p6">+</p>
                        </button>
                      </div>
                      <div className="text-right">
                        <p className="mobile-p6 font-bold text-black">
                          €{(getPriceWithVAT(product?.price || '0') * selectedQuantity).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <button
                  id="box-quantity-petid"
                  onClick={handleAddToCart}
                  disabled={product?.stock_status === 'outofstock' || isCheckoutLoading}
                  className="button-primary w-full bg-primary text-white rounded-full hover:bg-[#025a63] transition-colors flex items-center justify-center mt-6 mb-4 md:mobile-p2 mobile-p3 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isCheckoutLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Caricamento...
                    </>
                  ) : (
                    product?.stock_status === 'outofstock' ? 'Esaurito' : 'Aggiungi al carrello'
                  )}
                </button>

                {/* Shipping Info */}
                <div className="flex flex-col items-center justify-center gap-2 mb-4 p-3">
                  <p className="md:mobile-p4 mobile-p5 text-gray-2 text-center">
                    🇮🇹 | Spedizione gratis in tutta <strong>Italia</strong>
                  </p>
                  <p className="inline-flex items-center justify-center text-center px-3 py-1 rounded-full bg-white text-primary border border-primary/30 shadow-sm mobile-small font-medium">
                    3 misure incluse
                  </p>
                </div>

                {/* Payment Methods */}
                <div className="flex items-center justify-center gap-2 mb-4 flex-wrap">
                  <Image src="/assets/medal_res/payment_methods/Frame-2.svg" alt="Payment Method" width={38} height={24} />
                  <Image src="/assets/medal_res/payment_methods/Frame-3.svg" alt="Payment Method" width={38} height={24} />
                  <Image src="/assets/medal_res/payment_methods/Frame-4.svg" alt="Payment Method" width={38} height={24} />
                  <Image src="/assets/medal_res/payment_methods/Frame-5.svg" alt="Payment Method" width={38} height={24} />
                  <Image src="/assets/medal_res/payment_methods/Frame-7.svg" alt="Payment Method" width={38} height={24} />
                  <Image src="/assets/medal_res/payment_methods/Frame-8.svg" alt="Payment Method" width={38} height={24} />
                </div>

                {/* Guarantees */}
                {/* <div className="flex items-center justify-center gap-8">
                  <span className="paragraph-5 text-primary">2 anni di garanzia</span>
                  <span className="paragraph-5 text-primary">Spedizione gratuita</span>
                </div> */}
              </div>
            </div>
          </div>
        </div>

        {/* FAQ Accordion Section - Mobile only */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 md:hidden">
          <div className="space-y-3">
            {faqData.map((item, idx) => {
              const isOpen = openIndex === idx;
              return (
                <div
                  key={idx}
                  className={`bg-[#F9F8F8] p-4 rounded-lg transition-all duration-300`}
                >
                  <div
                    className="flex justify-between items-center cursor-pointer"
                    onClick={() => toggleFAQ(idx)}
                  >
                    <p className="md:mobile-p3 mobile-p4 font-bold text-[#333333]">
                      {item.question}
                    </p>
                    <p className="text-[#fafafa] md:mobile-p4 mobile-p5">
                      <img
                        src={
                          isOpen
                            ? "/assets/home-su.svg"
                            : "/assets/home-giu.svg"
                        }
                        alt="freccia su/giu"
                        className="w-5 h-5"
                      />
                    </p>
                  </div>
                  <div
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                      }`}
                  >
                    <p
                      className="md:mobile-p3 mobile-p4 mt-3 text-[#4F4F4F] text-md leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: item.answer }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ENPA Collaboration Section */}
        <section id="enpa-collaboration" className="bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            {/* Collaboration Content Box */}
            <div className="p-6 md:p-12">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
                {/* Content Column */}
                <div className="w-full md:col-span-7 order-1 md:order-1">
                  <p className="text-center md:text-left mobile-p3 md:mobile-p2 text-[#000000] mb-1.5">
                    Babalù® ❤️ ENPA
                  </p>
                  <h4 className="mobile-h4 md:h3 font-heading text-primary mb-4 no-bold text-center md:text-left">
                    Collaborazione tra <strong>Babalù®</strong> ed <strong>ENPA</strong>
                  </h4>
                  <p className="text-center md:text-left mobile-p3 md:mobile-p3 text-gray-1 mb-4">
                    <strong>Babalù®</strong> è orgogliosa di collaborare con <strong>ENPA</strong> (Ente Nazionale Protezione Animali) per promuovere il <strong>benessere</strong> e la <strong>sicurezza</strong> degli <strong>animali in Italia</strong>.
                    <br />
                    <br />
                    La medaglietta <strong>Babalù® PetID</strong> è stata sviluppata con l&apos;obiettivo di offrire una <strong>soluzione innovativa</strong> ed efficace per la <strong>protezione</strong> dei nostri amici a quattro zampe, anche grazie ai consigli e le indicazioni di ENPA.
                    <br />
                    <br />
                    Grazie a questa collaborazione, possiamo <strong>garantire</strong> che ogni pet abbia la possibilità di essere <strong>identificato rapidamente</strong> e in modo sicuro, <strong>facilitando il ricongiungimento</strong> con i propri padroni in caso di smarrimento.
                    <br />
                    <br />
                    Inoltre, con l&apos;acquisto di ogni medaglietta, una <strong>parte del ricavato sarà devoluta ad ENPA</strong>.
                  </p>
                </div>
                {/* Image Column */}
                <div className="w-full md:col-span-5 order-2 md:order-2">
                  <Image
                    src={parseImg("https://firebasestorage.googleapis.com/v0/b/balu-4523d.appspot.com/o/site_res%2Fmedal_res%2Fpartnership-enpa2.png?alt=media&token=d2bb3b0a-6ce0-4084-888d-255de3090099", "big")}
                    alt="ENPA Logo"
                    width={600}
                    height={600}
                    className="w-full h-auto object-contain"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Hanno parlato di noi Section */}
        <section className="py-12 bg-white">
          <div className="flex flex-col">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 mb-6">
              <p className="text-center mobile-p2 md:mobile-p1 font-bold text-[#000000]">Hanno parlato di Babalù®</p>
            </div>
            <HorizontalCarousel
              items={pressLogos}
              itemClassName="w-32 sm:w-40 h-16 sm:h-24 object-contain"
              gap="2rem"
              duration={30}
            />
          </div>
        </section>

        <section className="py-6 bg-white mb-21">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 mb-6">
            <p className="text-center mobile-p3 md:mobile-p2 text-[#000000] mb-1.5">La medaglietta 100% Made in Italy by Babalù®</p>
            <h5 className="mobile-h4 md:mobile-h3 font-heading text-primary mb-4 no-bold text-center"><strong>Super poteri per una vita serena!</strong></h5>
            <p className="text-center mobile-p2 md:mobile-p1 text-gray-2">Con Babalù®️ PetID hai tutto sotto controllo: libretto sanitario digitale, contatti immediati in caso di smarrimento e notifiche in tempo reale. Sicura, smart ed inimitabile… proprio come il tuo legame con lui. 💛
            </p>
            <p className="text-center mobile-p2 md:mobile-p1 text-gray-2 mt-4 font-bold">🐴 🐱 Adatta a tutte le specie e razze 🐶 🐷  </p>
          </div>
        </section>

        {/* Features Grid Section */}
        <section className="bg-white">
          <div className="w-full overflow-hidden">
            <div
              className="relative flex animate-scroll-infinite px-4 md:px-0"
              style={
                {
                  width: 'fit-content',
                  gap: '2rem',
                  animationDuration: '40s',
                } as CSSProperties
              }
            >
              {[...featuresData, ...featuresData].map((feature, index) => (
                <FeatureCard
                  key={index}
                  title={feature.title}
                  description={feature.description}
                  icon={feature.icon}
                  alt={feature.alt}
                />
              ))}
            </div>
          </div>
          <div className="max-w-6xl mx-auto px-4 sm:px-6">

            {/* Image Section */}
            <div className="mt-4">
              <div
                className="md:block w-full rounded-2xl overflow-hidden"
              >
                <VideoCard
                  className="w-full md:aspect-[9/16] md:h-[598px] z-40 object-cover"
                  posterSrc="/assets/thumb-medaglietta.png"
                  videoSrc="https://vz-ccd007ce-e29.b-cdn.net/716215fc-5e57-495c-be0a-0292c3e5dc8e/playlist.m3u8"
                  playOnViewport={true}
                />
              </div>
              <div className="w-full mt-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                  <div className="w-full text-right">
                    <p className="text-center md:text-right mobile-p3 md:mobile-p2 text-[#000000] mb-1.5">Babalù® PetID</p>
                    <h5 className="mobile-h5 md:mobile-h4 font-heading text-primary mb-4 no-bold text-center md:text-right">Il <strong>profilo completo</strong> del tuo pet a portata di <strong>codice QR</strong></h5>

                    <div className="flex flex-col items-center md:flex-row md:flex-wrap md:items-start md:justify-end gap-[6px] feature-boxes-desktop">
                      <FeatureBox
                        className="text-center md:text-right w-full md:w-auto flex-row-reverse md:flex-row justify-start md:justify-end"
                        text="Recapiti di emergenza <strong>illimitati</strong>"
                        classTextName="text-center md:text-right"
                        icon="/assets/medal_res/phone.svg"
                        alt="Recapiti di emergenza"
                      />
                      <FeatureBox
                        className="text-center md:text-right w-full md:w-auto flex-row-reverse md:flex-row justify-start md:justify-end"
                        text="<strong>Allergie</strong> e <strong>patologie</strong>"
                        classTextName="text-center md:text-right"
                        icon="/assets/medal_res/bone-off.svg"
                        alt="Allergie e patologie"
                      />
                      <FeatureBox
                        className="text-center md:text-right w-full md:w-auto flex-row-reverse md:flex-row justify-start md:justify-end"
                        text="Segnalazione della <strong>posizione</strong>"
                        classTextName="text-center md:text-right"
                        icon="/assets/medal_res/current-location.svg"
                        alt="Segnalazione posizione"
                      />
                      <FeatureBox
                        className="text-center md:text-right w-full md:w-auto flex-row-reverse md:flex-row justify-start md:justify-end"
                        text="<strong>Polizza</strong> sanitaria"
                        classTextName="text-center md:text-right"
                        icon="/assets/medal_res/heart-handshake.svg"
                        alt="Polizza sanitaria"
                      />
                      <FeatureBox
                        className="text-center md:text-right w-full md:w-auto flex-row-reverse md:flex-row justify-start md:justify-end"
                        text="<strong>Veterinario</strong> di riferimento"
                        classTextName="text-center md:text-right"
                        icon="/assets/medal_res/stethoscope.svg"
                        alt="Veterinario di riferimento"
                      />
                    </div>
                  </div>
                  <VideoImageOverlay
                    imageSrc={"https://firebasestorage.googleapis.com/v0/b/balu-4523d.appspot.com/o/site_res%2Fmedal_res%2Fdesc_cut_4.png?alt=media&token=cedf36fa-6aac-4efa-a5ab-fd84dda585a9"}
                    videoUrl="https://vz-ccd007ce-e29.b-cdn.net/aabb3026-53f7-46f7-9399-2edcf92ce3ad/play_480p.mp4"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-6 bg-white mb-21">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 mb-6">
            <p className="text-center mobile-p3 md:mobile-p2 text-[#000000] mb-1.5">Registrati in meno di 2 minuti!</p>
            <h5 className="mobile-h4 md:mobile-h3 font-heading text-primary mb-4 no-bold text-center"><strong>Attivazione immediata!</strong></h5>
            <p className="text-center mobile-p2 md:mobile-p1 text-gray-2"><strong>Nessun abbonamento</strong> o configurazione complessa: scansiona il QR Code, crea o collega il profilo del tuo pet, vivilo con la protezione attiva.   🐾 <strong>Facile, veloce e sempre più ricca</strong>, offri il meglio ovunque sia il tuo amico a 4 zampe. 💛</p>
          </div>
          <div className="max-w-6xl space-y-4 mx-auto px-4 sm:px-6">
            {/* Prima riga: cane1 + box info */}
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <Image
                  src={parseImg("https://firebasestorage.googleapis.com/v0/b/balu-4523d.appspot.com/o/site_res%2Fmedal_res%2Fcane1.png?alt=media&token=4a13e0f9-78f4-4f25-a1e7-bae31f83060e", "big")}
                  alt="cane1"
                  width={600}
                  height={400}
                  className="h-full w-full h-48 object-cover rounded-2xl"
                  loading="eager"
                  sizes="(max-width: 768px) 100vw, 50vw"
                  quality={85}
                />
              </div>
              <div className="flex-1 md:p-12 p-6 rounded-2xl flex flex-col justify-center" style={{ background: '#F6F6F6', border: '1px solid #0D8849' }}>
                <div className="flex items-center gap-2 mb-3">
                  <h4 className="mobile-h5 md:mobile-h4 text-primary text-gray-1">100% Made in Italy 🇮🇹</h4>
                </div>
                <p className="text-gray-700 mobile-p4 md:mobile-p3 mb-6 text-gray-1">La medaglietta Babalù® non è come tutte le altre</p>
                <ul className="mobile-p4 md:mobile-p3 text-gray-600 space-y-1 text-gray-1" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                  <li>• <strong>Dati garantiti in Italia:</strong> server italiani e conformità GDPR totale</li>
                  <li>• <strong>Materiali sicuri:</strong> polimeri brevettati in Italia non tossici per il tuo pet</li>
                  <li>• <strong>Produzione italiana:</strong> dalla progettazione alla realizzazione</li>
                  <li>• <strong>Privacy first:</strong> i tuoi dati non lasciano mai l&apos;Italia</li>
                </ul>
              </div>
            </div>

            {/* Seconda riga: cane2 e cane3 affiancati */}
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 aspect-ratio-1/1">
                <Image
                  src={parseImg("https://firebasestorage.googleapis.com/v0/b/balu-4523d.appspot.com/o/site_res%2Fmedal_res%2Fcane2.png?alt=media&token=8b519c6d-37d5-4f2a-a8f3-55f4fc9c30f1", "big")}
                  alt="cane2"
                  width={600}
                  height={600}
                  className="h-full w-full object-cover rounded-2xl"
                  loading="eager"
                  sizes="(max-width: 768px) 100vw, 50vw"
                  quality={85}
                />
              </div>
              <div className="flex-1 aspect-ratio-1/1">
                <Image
                  src={parseImg("https://firebasestorage.googleapis.com/v0/b/balu-4523d.appspot.com/o/site_res%2Fmedal_res%2Fcane3.png?alt=media&token=c9c0a01d-0203-46ce-8ad9-dfdf4fd5fb70", "big")}
                  alt="cane3"
                  width={600}
                  height={600}
                  className="h-full w-full object-cover rounded-2xl"
                  loading="eager"
                  sizes="(max-width: 768px) 100vw, 50vw"
                  quality={85}
                />
              </div>
            </div>
          </div>
        </section>

        {/* New Section */}
        <section className="py-12 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div className="w-full order-2 md:order-1">
                <Image
                  src={parseImg("https://firebasestorage.googleapis.com/v0/b/balu-4523d.appspot.com/o/site_res%2Fmedal_res%2Fdesc_petid_3.png?alt=media&token=4521bcc5-a4e3-4905-9e57-67c3f831f4ec", "big")}
                  alt="Descrizione PetID"
                  width={600}
                  height={600}
                  className="w-full h-auto"
                  loading="eager"
                  sizes="(max-width: 768px) 100vw, 50vw"
                  quality={85}
                />
              </div>
              <div className="w-full order-1 md:order-2">
                <p className="text-center md:text-left mobile-p3 md:mobile-p2 text-[#000000] mb-1.5">Super poteri dell&apos;app Babalù®</p>
                <h5 className="mobile-h5 md:mobile-h4 font-heading text-primary mb-4 no-bold text-center md:text-left">Il <strong>libretto sanitario digitale più completo</strong> al mondo</h5>
                <p className="text-center md:text-left mobile-p3 md:mobile-p2 text-gray-1 mb-4">
                  Realizzato in collaborazione ad un pool di Veterinari, gestisce al suo interno tutte le informazioni necessarie al monitoraggio del benessere animale ed una second opinion da remoto.
                </p>
                <FeatureBox
                  className="mb-4 flex-row-reverse"
                  text="<strong>Tutte</strong> le <strong>vaccinazioni</strong> in un unico posto"
                  subtitle="Riconoscimento automatico di tutti quelli già effettuati e suggerimento dei prossimi richiami"
                  classTextName="text-left"
                  icon="/assets/medal_res/empathize.svg"
                  alt="Riconoscimento automatico di tutti quelli già effettuati e suggerimento dei prossimi richiami"
                />
                <FeatureBox
                  className="mb-4 flex-row-reverse"
                  text="<strong>Patologie</strong> e <strong>intolleranze</strong>"
                  subtitle="Una scheda completa con tutte le informazioni importanti, dalla A alla Z"
                  classTextName="text-left"
                  icon="/assets/medal_res/report-medical.svg"
                  alt="Una scheda completa con tutte le informazioni importanti, dalla A alla Z"
                />
                <FeatureBox
                  className="mb-4 flex-row-reverse"
                  text="Carica <strong>documenti</strong>, <strong>esami</strong> e <strong>referti</strong>"
                  subtitle="Il tuo cloud con tutte le analisi, cartelle cliniche e referti, sempre con te"
                  classTextName="text-left"
                  icon="/assets/medal_res/file-upload.svg"
                  alt="Il tuo cloud con tutte le analisi, cartelle cliniche e referti, sempre con te"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Calendario Smart Section */}
        <section className="py-20" style={{ background: 'linear-gradient(180deg, #ECFDF5 0%, #FFFFFF 100%)' }}>
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
              <div className="md:col-span-7">
                <p className="text-center md:text-left mobile-p3 md:mobile-p2 text-[#000000] mb-1.5">Super poteri dell&apos;app Babalù®</p>
                <h4 className="mobile-h4 md:h3 font-heading text-primary mb-4 no-bold text-center md:text-left">Il <strong>primo calendario smart</strong> del tuo amico pet</h4>
                <p className="text-center md:text-left mobile-p2 md:mobile-p1 text-gray-1">
                  Tutte le informazioni, i reminder e gli eventi dei tuoi pet, in una sola schermata, integrabile con il tuo calendario Google!
                </p>
              </div>
              <div className="md:col-span-5">
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mt-8" style={{ alignItems: 'flex-end' }}>
              <div className="md:col-span-6">
                <div className="relative">
                  <video
                    src="https://vz-ccd007ce-e29.b-cdn.net/db83b9d2-c9b2-4306-9689-e2df80402275/play_720p.mp4"
                    className="w-full h-auto rounded-[24px]"
                    style={{ aspectRatio: '1', objectFit: 'cover' }}
                    autoPlay
                    loop
                    muted
                    playsInline
                  />
                  {/* Overlay */}
                  <div
                    className="absolute inset-0 rounded-[24px] pointer-events-none"
                    style={{
                      background: 'linear-gradient(0deg, rgba(2, 110, 120, 0.6), rgba(2, 110, 120, 0.1)), linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.4) 67.33%)',
                    }}
                  />
                  {/* Absolute positioned texts */}
                  <div className="absolute bottom-0 left-0 p-4 flex flex-col md:gap-12 gap-4 z-10">
                    <div className="flex flex-col md:gap-4 gap-2">
                      <p className="text-white mobile-p2 md:mobile-p1 font-bold">Richiami automatici</p>
                      <p className="text-white mobile-p4 md:mobile-p3">Babalù® è intelligente, riconosce i vaccini che hai già fatto e ti ricorda quando effettuare i richiami</p>
                    </div>
                    <div className="flex flex-col md:gap-4 gap-2">
                      <p className="text-white mobile-p2 md:mobile-p1 font-bold">Sincronizzato col tuo calendar</p>
                      <p className="text-white mobile-p4 md:mobile-p3">Fai fatica a conciliare tutti gli impegni con la tua vita professionale? Da oggi puoi sincronizzare gli eventi con il calendario del tuo cellulare</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="md:col-span-6 relative">
                <div style={{
                  position: 'absolute',
                  backgroundColor: '#026E78',
                  aspectRatio: '1 / 1',
                  width: '100%',
                  bottom: 0,
                  borderRadius: '24px'
                }}>

                </div>
                <Image
                  src={parseImg("https://firebasestorage.googleapis.com/v0/b/balu-4523d.appspot.com/o/site_res%2Fmedal_res%2Fcalendar.png?alt=media&token=f9245f01-9a80-4a9f-b523-56ea50e036cd", "big")}
                  alt="Calendario"
                  width={600}
                  height={600}
                  className="w-full h-auto rounded-[24px] calendar-image-desktop relative"
                  style={{
                    zIndex: 99
                  }}
                  loading="eager"
                  sizes="(max-width: 768px) 100vw, 50vw"
                  quality={85}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mt-8">
              <div className="md:col-span-5 order-2 md:order-1">
                <VideoImageOverlay
                  videoUrl="https://vz-ccd007ce-e29.b-cdn.net/7adbc6cd-59fd-47ba-8ca7-b80dfad736a6/play_720p.mp4"
                  imageSrc={"https://firebasestorage.googleapis.com/v0/b/balu-4523d.appspot.com/o/site_res%2Fmedal_res%2Fdesc_cut_1.png?alt=media&token=b9fb49c8-85f4-42d2-a7fb-6530a876288a"}
                  videoWidth="54%"
                  videoTranslateY="-39%"
                />
              </div>
              <div className="md:col-span-7 flex flex-col justify-center order-1 md:order-2">
                <p className="text-center md:text-left mobile-p3 md:mobile-p2 text-[#000000] mb-1.5">Super poteri dell&apos;app Babalù®</p>
                <h4 className="mobile-h4 md:h3 font-heading text-primary mb-4 no-bold text-center md:text-left"><strong>Digitalizza</strong> il tuo vecchio libretto cartaceo</h4>
                <p className="text-center md:text-left md:mobile-p2 mobile-p3 text-gray-1 mb-6">
                  Niente più ore ad inserire i dati, ti basta fotografare il libretto cartaceo e in automatico saranno recuperate tutte le informazioni utili
                </p>
                <ul className="space-y-4 text-center md:text-left list-disc" style={{ paddingLeft: '18px' }}>
                  <li className="md:mobile-p3 mobile-p3 text-gray-1">
                    <strong>Analisi Vaccini effettuati</strong> e predisposizione dei richiami
                  </li>
                  <li className="md:mobile-p3 mobile-p3 text-gray-1">
                    <strong>Recupero delle info principali:</strong> Nome, Razza, Chip...
                  </li>
                  <li className="md:mobile-p3 mobile-p3 text-gray-1">
                    Il tuo libretto sempre a portata di mano e facile da condividere con strutture e professionisti
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Tutti i professionisti Section */}
        <section className="py-12 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
            <p className="text-center mobile-p3 md:mobile-p2 text-[#000000] mb-1.5">Super poteri dell&apos;app Babalù®</p>
            <h4 className="mobile-h4 md:h3 font-heading text-primary mb-4 no-bold"><strong>Tutti i professionisti</strong> del mondo pet a portata di zampa</h4>
            <p className="mobile-p2 md:mobile-p1 text-gray-1 mb-6">
              Una <strong>mappa completa</strong> e <strong>geolocalizzata</strong> per cercare, confrontare e contattare tutti i migliori professionisti del mondo Pet.
            </p>
            <Image
              src={parseImg("https://firebasestorage.googleapis.com/v0/b/balu-4523d.appspot.com/o/site_res%2Fmedal_res%2Fprofessionisti.png?alt=media&token=74acef21-dcbd-48e3-beca-228363c2a2d6", "big")}
              alt="Professionisti del mondo pet"
              width={1200}
              height={600}
              className="w-full h-auto"
              loading="eager"
              sizes="100vw"
              quality={85}
            />
          </div>
        </section>

        {/* Recensioni Trustpilot Section */}
        <section className="bg-white" style={{ background: '#E8F5F7' }}>
          <div className="px-6">
            <div className="pt-16 max-w-6xl space-y-4 mx-auto px-4 sm:px-6">
              {/* Header */}
              <div className="text-center mb-6">
                <p className="mobile-p3 md:mobile-p2 text-[#000000] mb-1.5">Recensioni</p>
                <h4 className="mobile-h4 md:h3 font-heading mb-4">Cosa dicono i pet lover</h4>

                {/* 5 Stars */}
                <Image src="/assets/medal_res/trustpilot.svg" alt="5 stars" width={300} height={100} style={{ margin: '0 auto', marginTop: '16px' }} />
              </div>

              {/* Reviews */}
              <div className="space-y-4 md:grid md:grid-cols-2 md:gap-4 md:space-y-0">
                {reviewsData.map((review, index) => (
                  <div key={index} style={{ background: 'white', borderRadius: '12px', padding: '24px' }}>
                    <div className="flex mb-1">
                      {<Image
                        key={index}
                        src="/assets/medal_res/5-star.svg"
                        alt="Star"
                        width={132}
                        height={160}
                      />}
                    </div>
                    <p className="mobile-p3 md:mobile-p2 font-bold text-[#000000] mb-1">{review.title}</p>
                    <p className="mobile-p5 md:mobile-p4 text-gray-2">{review.content}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Media Gallery Section */}
        <ScrollingMedia mediaItems={mediaData} />

        {/* Domande Frequenti Section */}
        <section className="py-12 mb-16 md:mb-24 bg-white" id="faq">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h4 className="mobile-h5 md:mobile-h4 font-heading mb-8 md:mb-10 no-bold text-center"><strong>Domande frequenti</strong></h4>

            <div className="space-y-4 md:space-y-5">
              {frequentQuestionsData.map((item, idx) => {
                const isOpen = openIndex === idx + 100; // Offset per evitare conflitti con le altre FAQ
                return (
                  <div
                    key={idx}
                    className={`bg-[#F9F8F8] p-5 md:p-6 rounded-lg transition-all duration-300`}
                  >
                    <div
                      className="flex justify-between items-center cursor-pointer gap-4"
                      onClick={() => setOpenIndex(isOpen ? null : idx + 100)}
                    >
                      <p className="font-bold md:mobile-p2 mobile-p3 text-[#333333] flex-1">
                        {item.question}
                      </p>
                      <span className="text-[#4F4F4F] paragraph-2 flex-shrink-0">
                        <img
                          src={
                            isOpen
                              ? "/assets/home-su.svg"
                              : "/assets/home-giu.svg"
                          }
                          alt="freccia su/giu"
                          className="w-5 h-5"
                        />
                      </span>
                    </div>
                    <div
                      className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                        }`}
                    >
                      <p
                        className="mt-4 md:mt-5 text-[#4F4F4F] md:mobile-p3 mobile-p4 leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: item.answer }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Mobile Sticky Bottom Bar */}
        {product && (
          <div
            className={`fixed bottom-0 left-0 right-0 z-[1001] border-t border-gray-200 shadow-lg transition-transform duration-300 ${showStickyBar ? 'translate-y-0' : 'translate-y-full'}`}
            style={{
              background: 'linear-gradient(180deg, #d8e8ea, #ffffff)'
            }}
          >
            {/* Shipping Banner */}
            <div className="bg-[#DE5E3D] text-white text-center py-2 px-4 flex items-center justify-center gap-2">
              <span className="paragraph-4 font-bold">🇮🇹 Spedizione gratuita in tutta Italia!</span>
            </div>

            <div className="max-w-6xl mx-auto px-4 py-3">
              <div className="flex items-center gap-2.5">
                {/* Product Image */}
                <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                  <Image
                    src={product.images?.[0]?.src || "/assets/petid-icon.svg"}
                    alt="Babalù PetID"
                    width={64}
                    height={64}
                    className="w-full h-full object-cover rounded-[16px]"
                  />
                </div>

                {/* Product Info */}
                <div className="flex-1 min-w-0">
                  <p className="mobile-p4 font-bold text-black truncate">Babalù® PetID</p>
                  <p className="mobile-small text-gray-2 truncate">Medaglietta anti-smarrimento</p>
                  {(() => {
                    const unitPriceWithVAT = getPriceWithVAT(product?.price || '0');
                    const unitRegularPriceWithVAT = getPriceWithVAT(product?.regular_price || product?.price || '0');
                    const totalPrice = unitPriceWithVAT * selectedQuantity;
                    const totalRegularPrice = unitRegularPriceWithVAT * selectedQuantity;
                    const hasDiscount = product?.sale_price && product.sale_price !== product.regular_price;
                    return (
                      <div className="flex items-center gap-2 mt-1">
                        {hasDiscount && (
                          <span className="mobile-small line-through text-gray-2">
                            €{totalRegularPrice.toFixed(2)}
                          </span>
                        )}
                        <span className="mobile-p6 font-bold text-primary">
                          €{totalPrice.toFixed(2)}
                        </span>
                      </div>
                    );
                  })()}
                </div>

                {/* Buy Button */}
                <button
                  onClick={handleAddToCart}
                  disabled={product?.stock_status === 'outofstock' || isCheckoutLoading}
                  className="bg-primary text-white rounded-[12px] p-3 mobile-p4 hover:bg-[#025a63] transition-colors flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isCheckoutLoading ? (
                    <>
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Caricamento...
                    </>
                  ) : (
                    product?.stock_status === 'outofstock' ? 'Esaurito' : 'Aggiungi al carrello'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add padding-bottom to prevent content from being hidden behind sticky bar */}
        <Footer />

        {/* Image Modal */}
        {isImageModalOpen && selectedImage && product?.images && (
          <div
            className="z-[9999999] fixed inset-0 z-50 bg-black bg-opacity-80 flex items-center justify-center p-4"
            onClick={closeImageModal}
          >
            <div
              className="relative max-w-4xl max-h-full bg-white rounded-lg overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={closeImageModal}
                className="absolute top-4 right-4 z-10 bg-white bg-opacity-90 text-black rounded-full w-8 h-8 flex items-center justify-center hover:bg-opacity-100 transition-all"
              >
                ✕
              </button>

              {/* Previous Button */}
              {product.images.length > 2 && (
                <button
                  onClick={goToPreviousImage}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 bg-white bg-opacity-90 text-black rounded-full w-10 h-10 flex items-center justify-center hover:bg-opacity-100 transition-all"
                >
                  <Image
                    src="/assets/chevron-left.svg"
                    alt="Previous"
                    width={16}
                    height={16}
                    className="w-4 h-4"
                  />
                </button>
              )}

              {/* Next Button */}
              {product.images.length > 2 && (
                <button
                  onClick={goToNextImage}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 bg-white bg-opacity-90 text-black rounded-full w-10 h-10 flex items-center justify-center hover:bg-opacity-100 transition-all"
                >
                  <Image
                    src="/assets/chevron-right.svg"
                    alt="Next"
                    width={16}
                    height={16}
                    className="w-4 h-4"
                  />
                </button>
              )}

              {/* Image */}
              <Image
                src={selectedImage}
                alt="Product image"
                width={800}
                height={600}
                className="w-full h-full object-contain max-h-[90vh]"
              />

              {/* Image Counter */}
              {product.images.length > 2 && (
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-70 text-white px-3 py-1 rounded-full paragraph-6">
                  {selectedImageIndex} / {product.images.length - 1}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Animation styles */}
        <style jsx global>{`
        @keyframes scrollLoopReverse {
          0% {
            transform: translateX(0%);
          }
          100% {
            transform: translateX(-50%);
          }
        }
        
        .features-scroll {
          animation: scrollLoopReverse 20s linear infinite;
          -webkit-overflow-scrolling: touch;
        }
        
        @media (min-width: 768px) {
          .features-scroll {
            animation: scrollLoopReverse 40s linear infinite;
          }
          .media-item img,
          .media-item video {
            width: ${mediaData[0]?.width || 325}px !important;
            height: ${mediaData[0]?.height || 550}px !important;
          }
          .media-item:nth-child(even) img,
          .media-item:nth-child(even) video {
            width: ${mediaData[1]?.width || 335}px !important;
            height: ${mediaData[1]?.height || 482}px !important;
          }
        }
      `}</style>

        {/* Login Modal */}
        <LoginModal
          isOpen={isOpen}
          onClose={closeModal}
          isRegistration={isRegistration}
          isCheckoutFlow={isCheckoutFlow}
          onCheckoutReady={proceedToCheckout}
        />

        {/* Checkout Modal - Mostra link cliccabile per Safari */}
        {showCheckoutModal && checkoutUrl && (
          <div className="fixed inset-0 z-[1000] flex items-center justify-center px-4 bg-black bg-opacity-40" onClick={() => setShowCheckoutModal(false)}>
            <div className="bg-white rounded-[24px] p-8 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-[#026E78] text-2xl font-bold mb-4">Aggiungi al carrello</h3>
              <p className="text-[#4F4F4F] text-base mb-4">
                Clicca sul link qui sotto per aprire il carrello in una nuova scheda:
              </p>
              <div className="flex flex-col gap-3">
                <a
                  href={checkoutUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#026E78] text-base break-all underline hover:text-[#024a52]"
                >
                  {checkoutUrl}
                </a>
                <button
                  onClick={() => {
                    setShowCheckoutModal(false);
                    setCheckoutUrl(null);
                  }}
                  className="w-full text-center rounded-lg border border-[#026E78] px-4 py-2 bg-[#026E78] text-white text-base cursor-pointer hover:bg-[#024a52]"
                >
                  Chiudi
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default function PetIDPage() {
  return (
    <Suspense fallback={<div>Caricamento...</div>}>
      <PetIDPageContent />
    </Suspense>
  );
}
